import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingBag, Columns, Plus, Package,
  Users, UserCog, Wallet, Settings, Menu, X, LogOut, Globe,
  CalendarDays, MessageCircle, TrendingUp, Receipt, FileText, PieChart,
  UserCheck, CreditCard, Boxes, BookOpen
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { waApi } from '../services/api';
import QuickAccessMenu from '../components/QuickAccessMenu';
import FloatingQuickAccessButton from '../components/FloatingQuickAccessButton';

const CALENDAR_TYPES = ['rental', 'appointment', 'mua', 'penjahit'];

const NAV_GROUPS = [
  {
    groupId: 'main',
    label: null,
    items: [
      { path: '/admin', icon: LayoutDashboard, id: 'Dashboard', en: 'Dashboard', exact: true },
      { path: '/admin/tutorials', icon: BookOpen, id: 'Pusat Tutorial', en: 'Tutorial Center', ownerOnly: true },
    ],
  },
  {
    groupId: 'pesanan',
    label: { id: 'Pesanan', en: 'Orders' },
    items: [
      { path: '/admin/order-create', icon: Plus, id: 'Pesanan Baru', en: 'New Order' },
      { path: '/admin/orders', icon: ShoppingBag, id: 'Daftar Pesanan', en: 'All Orders' },
      { path: '/admin/order-board', icon: Columns, id: 'Kanban Board', en: 'Kanban' },
    ],
  },
  {
    groupId: 'crm',
    label: { id: 'CRM', en: 'CRM' },
    items: [
      { path: '/admin/customers', icon: Users, id: 'Pelanggan', en: 'Customers' },
      { path: '/admin/segments', icon: PieChart, id: 'Segmentasi', en: 'Segments' },
      { path: '/admin/wa-log', icon: MessageCircle, id: 'WA Log', en: 'WA Log' },
    ],
  },
  {
    groupId: 'laporan',
    label: { id: 'Laporan', en: 'Reports' },
    items: [
      { path: '/admin/reports/profit-loss', icon: TrendingUp, id: 'Laba Rugi', en: 'Profit & Loss' },
      { path: '/admin/reports/costs', icon: Receipt, id: 'Laporan Biaya', en: 'Costs' },
      { path: '/admin/reports/staff', icon: UserCheck, id: 'Kinerja Karyawan', en: 'Staff Perf.' },
      { path: '/admin/reports/receivables', icon: CreditCard, id: 'Piutang', en: 'Receivables' },
    ],
  },
  {
    groupId: 'calendar',
    label: null,
    calendarOnly: true,
    items: [
      { path: '/admin/calendar', icon: CalendarDays, id: 'Kalender', en: 'Calendar' },
    ],
  },
  {
    groupId: 'bisnis',
    label: { id: 'Kelola Bisnis', en: 'Business' },
    items: [
      { path: '/admin/products', icon: Package, id: 'Produk', en: 'Products' },
      { path: '/admin/inventory', icon: Boxes, id: 'Inventory', en: 'Inventory', inventoryOnly: true },
      { path: '/admin/cash-drawer', icon: Wallet, id: 'Kas', en: 'Cash Drawer' },
      { path: '/admin/biaya', icon: Receipt, id: 'Biaya Operasional', en: 'Operational Costs' },
      { path: '/admin/staff', icon: UserCog, id: 'Karyawan', en: 'Staff' },
      { path: '/admin/settings', icon: Settings, id: 'Pengaturan', en: 'Settings' },
    ],
  },
];

function NavItem({ item, lang, onClick }) {
  const location = useLocation();
  const active = item.exact
    ? location.pathname === item.path
    : location.pathname.startsWith(item.path);
  return (
    <Link
      to={item.path}
      onClick={onClick}
      data-testid={`nav-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-150 ${
        active
          ? 'bg-[#0f6c44] text-white shadow-sm'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
      }`}
    >
      <item.icon size={16} />
      <span>{lang === 'id' ? item.id : item.en}</span>
    </Link>
  );
}

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quickAccessOpen, setQuickAccessOpen] = useState(false);
  const navigate = useNavigate();
  const { user, store, logout, businessType } = useAuth();
  const { lang, toggleLang } = useLanguage();
  const showCalendar = CALENDAR_TYPES.includes(businessType);
  const { data: waStatus } = useQuery({
    queryKey: ['wa-status'],
    queryFn: () => waApi.getStatus().then(result => result.data),
    refetchInterval: 60000,
    retry: 1,
  });
  const waConnected = Boolean(waStatus?.connected);
  const isDemoStore = Boolean(store?.is_demo_store);

  const handleLogout = async () => {
    await logout();
    toast.success('Berhasil keluar');
    navigate('/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#0f6c44] rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold font-manrope">K</span>
          </div>
          <div className="min-w-0">
            <p className="font-bold text-slate-800 text-sm font-manrope">KasirWA</p>
            <p className="text-xs text-slate-400 truncate">{user?.store_name || 'POS System'}</p>
            <span data-testid="wa-connection-status" className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${waConnected ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${waConnected ? 'bg-emerald-500' : 'bg-rose-500'}`} />
              {isDemoStore ? 'WA Terhubung (Demo Mode)' : waStatus ? (waConnected ? 'WA Terhubung' : 'WA Terputus') : 'Memeriksa WA'}
            </span>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-4 overflow-y-auto" data-testid="sidebar-nav">
        {NAV_GROUPS.map(group => {
          if (group.calendarOnly && !showCalendar) return null;
          return (
            <div key={group.groupId}>
              {group.label && (
                <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 select-none">
                  {lang === 'id' ? group.label.id : group.label.en}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.filter(item => (!item.inventoryOnly || user?.store?.inventory_enabled) && (!item.ownerOnly || user?.role === 'owner')).map(item => (
                  <NavItem key={item.path} item={item} lang={lang} onClick={() => setSidebarOpen(false)} />
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="p-3 border-t border-slate-100 space-y-1">
        <button onClick={toggleLang}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 rounded-xl transition-colors">
          <Globe size={14} />
          <span>{lang === 'id' ? '🇬🇧 English' : '🇮🇩 Bahasa'}</span>
        </button>
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-[#0f6c44] text-xs font-bold">{(user?.name || 'U').charAt(0).toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-800 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
          </div>
        </div>
        <button onClick={handleLogout} data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors">
          <LogOut size={15} />
          <span>{lang === 'id' ? 'Keluar' : 'Logout'}</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      <aside className="hidden lg:flex flex-col w-60 bg-white border-r border-slate-100 fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={`fixed inset-y-0 left-0 w-60 bg-white border-r border-slate-100 z-50 transform transition-transform duration-300 lg:hidden ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="absolute top-3 right-3">
          <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X size={18} className="text-slate-500" />
          </button>
        </div>
        <SidebarContent />
      </aside>

      <main className="flex-1 lg:ml-60 flex flex-col min-h-screen">
        <header className="bg-white border-b border-slate-100 px-4 py-3 flex items-center gap-3 lg:hidden sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
            <Menu size={20} className="text-slate-600" />
          </button>
          <span className="font-bold text-slate-800 font-manrope">KasirWA</span>
        </header>
        {isDemoStore && <div data-testid="demo-mode-banner" className="mx-4 mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900 md:mx-6"><strong>WA Terhubung (Demo Mode) — Simulasi aktif.</strong> Data direset setiap hari dan pesan WhatsApp hanya dicatat di WA Log, tidak dikirim ke WhatsApp asli.</div>}
        <div className="flex-1 p-4 md:p-6 animate-fade-in">
          {children}
        </div>
      </main>
      <FloatingQuickAccessButton onClick={() => setQuickAccessOpen(true)} hidden={quickAccessOpen || sidebarOpen} />
      <QuickAccessMenu open={quickAccessOpen} onClose={() => setQuickAccessOpen(false)} userRole={user?.role} />
    </div>
  );
}
